import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import classNames from 'classnames';
import { DocumentScanLlmTraceResult } from 'common/lib/model/documentScan/DocumentScanTrace';
import Button from 'src/components/Button';
import Icon from 'src/components/Icon';
import ModalHeader from 'src/components/ModalHeader';
import SideModal from 'src/components/SideModal';
import { buildLlmTraceViewModels } from 'src/containers/DocumentScan/llmTraceViewModel';
import { ICONS } from 'src/utils/constants';
import {
    DocumentScanCaptureMode,
    DocumentScanSupportedEntityType,
    DocumentScanTypePreviewEvent,
    DocumentScanTypePreviewStage,
    DocumentScanTypeTestResult,
    DocumentScanTypeVersionSummary,
} from 'src/queries/useDocumentScanTypesAdmin';
import styles from './styles.module.scss';

export interface DocumentTypePreviewImage {
    id: string;
    fileName: string;
    mimeType: string;
    imageBase64: string;
    sizeBytes?: number;
    slotKey?: string;
    displayName?: string;
}

export interface DocumentTypePreviewRequest {
    simulationPath: 'new_encounter' | 'update_encounter';
    images: DocumentTypePreviewImage[];
}

interface CaptureSlot {
    slotKey: string;
    label: string;
    required: boolean;
    maxImages: number;
}

type StageStatus = 'waiting' | 'running' | 'complete' | 'failed';
type AdvancedPanel = 'ocr' | 'trace' | 'technical' | 'json';

interface TestState {
    simulationPath: DocumentTypePreviewRequest['simulationPath'];
    images: DocumentTypePreviewImage[];
    error: string | null;
    events: DocumentScanTypePreviewEvent[];
    expandedStage: DocumentScanTypePreviewStage;
}

type TestAction =
    | { type: 'reset' }
    | { type: 'simulationPath'; simulationPath: TestState['simulationPath'] }
    | { type: 'images'; images: DocumentTypePreviewImage[] }
    | { type: 'error'; error: string | null }
    | { type: 'begin' }
    | { type: 'event'; event: DocumentScanTypePreviewEvent }
    | { type: 'expandedStage'; stage: DocumentScanTypePreviewStage };

const MAX_IMAGE_BYTES = 15 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/heic,image/heif';
const stages: Array<{
    id: DocumentScanTypePreviewStage;
    title: string;
    description: string;
    icon: string;
}> = [
    {
        id: 'ocr',
        title: 'Read the document',
        description: 'Uses Apple text recognition to find the words in each image.',
        icon: 'file-alt',
    },
    {
        id: 'extraction',
        title: 'Extract information',
        description: 'Uses your prompt and field setup to pull out the information you asked for.',
        icon: 'magic',
    },
    {
        id: 'mapping',
        title: 'Prepare encounter changes',
        description: 'Maps the extracted information to encounter fields and shows what would happen.',
        icon: 'exchange-alt',
    },
];

function reducer(state: TestState, action: TestAction): TestState {
    switch (action.type) {
        case 'reset':
            return { simulationPath: 'new_encounter', images: [], error: null, events: [], expandedStage: 'ocr' };
        case 'simulationPath':
            return { ...state, simulationPath: action.simulationPath, error: null };
        case 'images':
            return { ...state, images: action.images, error: null };
        case 'error':
            return { ...state, error: action.error };
        case 'begin':
            return { ...state, error: null, events: [], expandedStage: 'ocr' };
        case 'event':
            return {
                ...state,
                events: [...state.events, action.event],
                expandedStage:
                    action.event.type === 'stage' &&
                    (action.event.status === 'running' || action.event.status === 'failed')
                        ? action.event.stage
                        : state.expandedStage,
            };
        case 'expandedStage':
            return { ...state, expandedStage: action.stage };
        default:
            return state;
    }
}

function getCaptureSlots(captureConfig: Record<string, unknown>): CaptureSlot[] {
    const slots = Array.isArray(captureConfig.slots) ? captureConfig.slots : [];
    return slots
        .filter((slot): slot is Record<string, unknown> => !!slot && typeof slot === 'object' && !Array.isArray(slot))
        .map((slot, index) => ({
            slotKey: `${slot.slotKey || `slot_${index + 1}`}`,
            label: `${slot.label || slot.slotKey || `Slot ${index + 1}`}`,
            required: slot.required === true,
            maxImages: Math.max(1, Number(slot.maxImages || 1)),
        }));
}

function readImage(file: File, id: string, slot?: CaptureSlot): Promise<DocumentTypePreviewImage> {
    if (file.size > MAX_IMAGE_BYTES) {
        return Promise.reject(new Error(`${file.name} is too large. Upload a smaller image (maximum 15 MB).`));
    }
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = `${reader.result || ''}`;
            const extension = file.name.split('.').pop()?.toLowerCase();
            const inferredMimeType =
                extension === 'png'
                    ? 'image/png'
                    : extension === 'heic'
                    ? 'image/heic'
                    : extension === 'heif'
                    ? 'image/heif'
                    : 'image/jpeg';
            resolve({
                id,
                fileName: file.name,
                mimeType: file.type || inferredMimeType,
                imageBase64: dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : dataUrl,
                sizeBytes: file.size,
                slotKey: slot?.slotKey,
                displayName: slot?.label || file.name,
            });
        };
        reader.onerror = () => reject(new Error(`Unable to read ${file.name}`));
        reader.readAsDataURL(file);
    });
}

function formatFileSize(sizeBytes?: number) {
    if (!sizeBytes) return '';
    return sizeBytes >= 1024 * 1024
        ? `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.max(1, Math.round(sizeBytes / 1024))} KB`;
}

function formatValue(value: unknown): string {
    if (value === undefined || value === null || value === '') return 'No value found';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value);
    return `${value}`;
}

function humanize(value: string) {
    const text = value
        .replace(/\[(\d+)\]/g, ' $1')
        .replace(/[._]/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .trim();
    return text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : value;
}

function flattenValues(value: unknown, prefix = ''): Array<{ path: string; value: unknown }> {
    if (!value || typeof value !== 'object' || value instanceof Date) {
        return prefix ? [{ path: prefix, value }] : [];
    }
    if (Array.isArray(value)) {
        return value.length ? value.flatMap((item, index) => flattenValues(item, `${prefix}[${index}]`)) : [];
    }
    return Object.entries(value as Record<string, unknown>).flatMap(([key, child]) =>
        flattenValues(child, prefix ? `${prefix}.${key}` : key),
    );
}

function getSchemaOrderedFields(value: Record<string, unknown>, schema?: Record<string, unknown> | null) {
    const flattened = flattenValues(value);
    const properties =
        schema?.properties && typeof schema.properties === 'object'
            ? (schema.properties as Record<string, unknown>)
            : null;
    if (!properties) return flattened;

    const includedPaths = new Set<string>();
    const ordered = Object.keys(properties).flatMap((propertyName) => {
        const propertyFields = flattened.filter(
            (field) =>
                field.path === propertyName ||
                field.path.startsWith(`${propertyName}.`) ||
                field.path.startsWith(`${propertyName}[`),
        );
        if (!propertyFields.length) return [{ path: propertyName, value: undefined }];
        propertyFields.forEach((field) => includedPaths.add(field.path));
        return propertyFields;
    });
    return [...ordered, ...flattened.filter((field) => !includedPaths.has(field.path))];
}

function getFieldLabel(path: string, schema?: Record<string, unknown> | null) {
    const rootKey = path.split(/[.[]/)[0];
    const properties =
        schema?.properties && typeof schema.properties === 'object'
            ? (schema.properties as Record<string, Record<string, unknown>>)
            : {};
    return `${properties[rootKey]?.title || humanize(path)}`;
}

function stripOcrMetadata(value = '') {
    return value
        .replace(/\s*\[ocrBlockId:[^\]]+\]\s*/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}

function getFriendlyError(error: { stage: string; code?: string; message: string }) {
    const message = (error.message || '').trim();
    if (/too large|15 mb|payload too large|request entity too large/i.test(message)) {
        return 'This image is too large. Upload a smaller image (maximum 15 MB) and try again.';
    }
    if (/at least one image must contain ocr data|no text|text.*not found/i.test(message)) {
        return 'We could not find readable text. Try a clearer image with the document filling more of the frame.';
    }
    if (error.stage === 'ocr' || /ocr|apple vision|service unavailable|http 5\d\d/i.test(message)) {
        return `We could not read the image right now. Reason: ${
            message || 'The text-recognition service did not provide a reason.'
        } Check that the Apple OCR service is available, then try again.`;
    }
    if (error.stage === 'analysis' || /ai|llm|extraction|timed out/i.test(message)) {
        return `The document was read, but information extraction could not finish. Reason: ${
            message || 'The extraction service did not provide a reason.'
        } Review the prompt and field setup, then try again.`;
    }
    if (error.stage === 'previewPlanning') {
        const missingFields = message.match(/required fields:\s*(.+?)(?:\.|$)/i)?.[1];
        if (missingFields) {
            return `The document was read and information was extracted, but a new encounter number could not be generated because these required values were not found: ${missingFields}. Confirm that the document contains these values and that the field names in Data Extraction match the encounter-number expression.`;
        }
        if (/no encounter number was extracted or generated/i.test(message)) {
            return 'The document was read and information was extracted, but no encounter number was extracted or generated. In Encounter Settings, configure an encounter-number expression or map an extracted value to the encounter number.';
        }
        return `The document was read and information was extracted, but the encounter changes could not be prepared. Reason: ${
            message || 'The encounter planner did not provide a reason.'
        } Review Encounter Settings and try again.`;
    }
    if (error.stage === 'captureValidation') return `The selected images do not match this configuration. ${message}`;
    return `The test could not finish. Reason: ${message || 'The service did not provide a reason.'}`;
}

function deriveStageStatus(
    stage: DocumentScanTypePreviewStage,
    events: DocumentScanTypePreviewEvent[],
    result: DocumentScanTypeTestResult | null,
): StageStatus {
    const streamed = [...events].reverse().find((event) => event.type === 'stage' && event.stage === stage) as
        | Extract<DocumentScanTypePreviewEvent, { type: 'stage' }>
        | undefined;
    const streamFailed = events.some((event) => event.type === 'stream_error');
    const lastStartedStage = [...events].reverse().find((event) => event.type === 'stage') as
        | Extract<DocumentScanTypePreviewEvent, { type: 'stage' }>
        | undefined;
    if (streamFailed && lastStartedStage?.stage === stage) return 'failed';
    if (streamed) return streamed.status;
    if (!result) return 'waiting';
    if (stage === 'ocr' && result.ocr?.length) return 'complete';
    if (stage === 'extraction' && result.extractedData) return 'complete';
    if (stage === 'mapping' && result.proposedAction) return 'complete';
    const names =
        stage === 'ocr' ? ['captureValidation', 'ocr'] : stage === 'extraction' ? ['analysis'] : ['previewPlanning'];
    const matching = result.steps.filter((step) => names.includes(step.step));
    if (matching.some((step) => step.status === 'failed')) return 'failed';
    if (matching.some((step) => step.status === 'complete')) return 'complete';
    return 'waiting';
}

function StatusIndicator({ status }: { status: StageStatus }) {
    if (status === 'running') {
        return <span className={styles.testProgressSpinner} data-test-id="document-type-test-progress-spinner" />;
    }
    const icon = status === 'complete' ? 'check' : status === 'failed' ? 'exclamation' : 'clock';
    return <Icon name={icon} type="fa" aria-hidden="true" />;
}

function statusLabel(status: StageStatus) {
    return status === 'running'
        ? 'In progress'
        : status === 'complete'
        ? 'Complete'
        : status === 'failed'
        ? 'Needs attention'
        : 'Waiting';
}

function OutcomeCard({ result }: { result: DocumentScanTypeTestResult }) {
    const action = result.proposedAction?.action;
    const count = result.proposedChanges?.updatedFieldCount || 0;
    const title =
        action === 'create'
            ? 'This configuration would create a new encounter.'
            : action === 'update'
            ? `This configuration would update ${count} encounter field${count === 1 ? '' : 's'}.`
            : action === 'review_required'
            ? 'This document would be sent for review.'
            : 'No encounter changes would be made.';
    return (
        <section className={styles.testOutcome} data-test-id="document-type-test-outcome">
            <div className={styles.testOutcomeIcon}>
                <Icon name={ICONS.CheckCircle} size={24} aria-hidden="true" />
            </div>
            <div>
                <span>Test complete</span>
                <h3>{title}</h3>
                {result.renderedEncounterNumber ? (
                    <div className={styles.encounterNumberResult}>
                        <small>Encounter number</small>
                        <strong>{result.renderedEncounterNumber}</strong>
                    </div>
                ) : null}
            </div>
        </section>
    );
}

function FieldList({ value, schema }: { value?: Record<string, unknown>; schema?: Record<string, unknown> | null }) {
    const fields = getSchemaOrderedFields(value || {}, schema);
    if (!fields.length) return <div className={styles.testEmptyResult}>No fields were extracted.</div>;
    return (
        <dl className={styles.testFieldList}>
            {fields.map((field) => (
                <div key={field.path}>
                    <dt>{getFieldLabel(field.path, schema)}</dt>
                    <dd>{formatValue(field.value)}</dd>
                </div>
            ))}
        </dl>
    );
}

function MappingResults({ result }: { result: DocumentScanTypeTestResult }) {
    const updatedFields = result.proposedChanges?.updatedFields || [];
    const action = result.proposedAction?.action;
    const mappedPaths = new Set(flattenValues(result.mappedEncounterData || {}).map((field) => field.path));
    const rows =
        action === 'create'
            ? flattenValues(result.proposedEncounterData || {}).map((field) => ({
                  source: mappedPaths.has(field.path) ? field.path : 'Configuration default',
                  target: field.path,
                  value: field.value,
                  action: 'Set on the new encounter',
              }))
            : updatedFields.length
            ? updatedFields.map((field) => ({
                  source: field.sourcePath || field.label,
                  target: field.targetPath,
                  value: field.value,
                  action: result.proposedAction?.action === 'review_required' ? 'Send for review' : 'Update this field',
              }))
            : flattenValues(result.mappedEncounterData || {}).map((field) => ({
                  source: field.path,
                  target: field.path,
                  value: field.value,
                  action:
                      result.proposedAction?.action === 'create'
                          ? 'Set on the new encounter'
                          : result.proposedAction?.action === 'none'
                          ? 'No change'
                          : 'Update this field',
              }));
    if (!rows.length) return <div className={styles.testEmptyResult}>No encounter fields were mapped.</div>;
    return (
        <div className={styles.testMappingTable} role="table" aria-label="Proposed encounter field changes">
            <div className={styles.testMappingHeader} role="row">
                <span role="columnheader">Extracted value</span>
                <span role="columnheader">Encounter field</span>
                <span role="columnheader">What would happen</span>
            </div>
            {rows.map((row, index) => (
                <div className={styles.testMappingRow} role="row" key={`${row.target}-${index}`}>
                    <span role="cell">
                        <small>{humanize(row.source)}</small>
                        <strong>{formatValue(row.value)}</strong>
                    </span>
                    <span role="cell">{humanize(row.target)}</span>
                    <span role="cell">{row.action}</span>
                </div>
            ))}
        </div>
    );
}

function buildPreviewTraceResults(result: DocumentScanTypeTestResult): DocumentScanLlmTraceResult[] {
    if (!result.advancedTrace) return [];
    const trace = result.advancedTrace;
    const createdAt = result.testContext?.executedAt || new Date().toISOString();
    const reference = {
        traceKey: 'document-type-test-preview',
        createdAt,
        processingAction: 'document_type_test_preview',
        provider: trace.provider,
        model: trace.model,
        temperature: trace.temperature,
        structuredOutput: trace.structuredOutput,
        durationMs: trace.durationMs,
        usage: trace.usage || null,
        documentScanTypeId: result.version?.documentScanTypeId || null,
        documentScanTypeVersionId: result.version?.documentScanTypeVersionId || null,
        documentScanTypeVersionNumber: result.version?.documentScanTypeVersionNumber || null,
        facilityDocumentScanTypeId: result.version?.facilityDocumentScanTypeId || null,
        facilityDocumentScanTypeVersionId: result.version?.facilityDocumentScanTypeVersionId || null,
        facilityDocumentScanTypeVersionNumber: result.version?.facilityDocumentScanTypeVersionNumber || null,
        imageAssetIds: [] as string[],
        ocrAssetIds: [] as string[],
    };
    return [{ reference, trace: { createdAt, ...reference, requests: [trace] } }];
}

function AdvancedResults({ result }: { result: DocumentScanTypeTestResult }) {
    const [panel, setPanel] = useState<AdvancedPanel>('ocr');
    const traceResults = useMemo(() => buildPreviewTraceResults(result), [result]);
    const traces = useMemo(() => buildLlmTraceViewModels(traceResults), [traceResults]);
    const tabs: Array<{ id: AdvancedPanel; label: string }> = [
        { id: 'ocr', label: 'Recognized text' },
        { id: 'trace', label: 'AI request & response' },
        { id: 'technical', label: 'Technical details' },
        { id: 'json', label: 'Raw JSON' },
    ];
    return (
        <details className={styles.testAdvanced} data-test-id="document-type-test-advanced">
            <summary>
                <Icon className={styles.testAdvancedChevron} name="chevron-right" type="fa" aria-hidden="true" />
                <span>
                    <strong>Advanced details</strong>
                    <small>View recognized text, the AI request and response, usage, timings, and raw JSON.</small>
                </span>
            </summary>
            <div className={styles.testAdvancedWarning}>
                Advanced details may contain information from the images you uploaded.
            </div>
            <div className={styles.testAdvancedTabs} role="tablist" aria-label="Advanced test details">
                {tabs.map((tab) => (
                    <button
                        type="button"
                        role="tab"
                        aria-selected={panel === tab.id}
                        className={classNames({ [styles.testAdvancedTabActive]: panel === tab.id })}
                        onClick={() => setPanel(tab.id)}
                        key={tab.id}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
            <div className={styles.testAdvancedPanel} role="tabpanel">
                {panel === 'ocr' ? (
                    (result.ocr || []).map((image) => (
                        <section key={image.id}>
                            <strong>{image.fileName}</strong>
                            <pre>{image.ocrData || image.error || 'No text found.'}</pre>
                        </section>
                    ))
                ) : panel === 'trace' ? (
                    traces.length ? (
                        traces.flatMap((trace) =>
                            trace.requests.map((request, requestIndex) => (
                                <section key={`${trace.key}-${requestIndex}`}>
                                    <div className={styles.testTraceSummary}>
                                        <span>{request.provider || 'Provider unavailable'}</span>
                                        <span>{request.model || 'Model unavailable'}</span>
                                        <span>{request.durationMs ?? '—'} ms</span>
                                    </div>
                                    {request.messages.map((message, messageIndex) => (
                                        <div
                                            className={styles.testTraceMessage}
                                            key={`${message.role}-${messageIndex}`}
                                        >
                                            <strong>{message.role}</strong>
                                            <pre>{formatValue(message.content)}</pre>
                                        </div>
                                    ))}
                                    <div className={styles.testTraceMessage}>
                                        <strong>Response</strong>
                                        <pre>{formatValue(request.responseContent)}</pre>
                                    </div>
                                </section>
                            )),
                        )
                    ) : (
                        <div className={styles.testEmptyResult}>No AI trace was returned.</div>
                    )
                ) : panel === 'technical' ? (
                    <FieldList
                        value={{
                            provider: result.advancedTrace?.provider,
                            model: result.advancedTrace?.model,
                            runtimeConfig: result.advancedTrace?.runtimeConfig,
                            temperature: result.advancedTrace?.temperature,
                            structuredOutput: result.advancedTrace?.structuredOutput,
                            modelUsage: result.modelUsage,
                            timings: result.timings,
                        }}
                    />
                ) : (
                    <pre>{JSON.stringify(result, null, 2)}</pre>
                )}
            </div>
        </details>
    );
}

function StageContent({
    stage,
    result,
    extractionSchema,
}: {
    stage: DocumentScanTypePreviewStage;
    result: DocumentScanTypeTestResult | null;
    extractionSchema?: Record<string, unknown> | null;
}) {
    if (!result) return null;
    if (stage === 'ocr') {
        return (
            <div className={styles.testOcrResults}>
                {(result.ocr || []).map((image) => (
                    <section key={image.id} data-test-id={`document-type-test-ocr-${image.id}`}>
                        <div>
                            <strong>{image.fileName}</strong>
                            <small>{image.executionTimeMs} ms</small>
                        </div>
                        {image.ocrData ? <p>{stripOcrMetadata(image.ocrData)}</p> : <p>No readable text was found.</p>}
                    </section>
                ))}
            </div>
        );
    }
    if (stage === 'extraction') return <FieldList value={result.extractedData} schema={extractionSchema} />;
    return <MappingResults result={result} />;
}

function TestTimeline({
    events,
    result,
    expandedStage,
    onExpand,
    extractionSchema,
}: {
    events: DocumentScanTypePreviewEvent[];
    result: DocumentScanTypeTestResult | null;
    expandedStage: DocumentScanTypePreviewStage;
    onExpand: (stage: DocumentScanTypePreviewStage) => void;
    extractionSchema?: Record<string, unknown> | null;
}) {
    const validationEvent = [...events].reverse().find((event) => event.type === 'validation') as
        | Extract<DocumentScanTypePreviewEvent, { type: 'validation' }>
        | undefined;
    const validationStatus = validationEvent?.status || (result ? 'complete' : 'waiting');
    return (
        <section className={styles.testTimelineSection} aria-labelledby="test-timeline-heading">
            <div className={styles.testSectionHeading}>
                <span>How this test works</span>
                <h3 id="test-timeline-heading">Follow the document from image to encounter</h3>
            </div>
            <div
                className={classNames(styles.testPreflight, {
                    [styles.testPreflightComplete]: validationStatus === 'complete',
                })}
            >
                {validationStatus === 'running' ? (
                    <span className={styles.testProgressSpinner} data-test-id="document-type-test-progress-spinner" />
                ) : (
                    <Icon
                        name={
                            validationStatus === 'failed'
                                ? 'exclamation-circle'
                                : validationStatus === 'complete'
                                ? 'check-circle'
                                : 'clock'
                        }
                        type="fa"
                        aria-hidden="true"
                    />
                )}
                <span>
                    {validationStatus === 'running'
                        ? 'Checking the configuration…'
                        : validationStatus === 'failed'
                        ? 'The configuration needs attention before testing.'
                        : validationStatus === 'complete'
                        ? 'Configuration checked'
                        : 'The configuration will be checked before the images are read.'}
                </span>
            </div>
            <ol className={styles.testTimeline} aria-live="polite">
                {stages.map((stage, index) => {
                    const status = deriveStageStatus(stage.id, events, result);
                    const expanded = expandedStage === stage.id;
                    const error = result?.errors?.find((item) =>
                        stage.id === 'ocr'
                            ? item.stage === 'ocr' || item.stage === 'captureValidation'
                            : stage.id === 'extraction'
                            ? item.stage === 'analysis'
                            : item.stage === 'previewPlanning',
                    );
                    const streamError = [...events].reverse().find((event) => event.type === 'stream_error') as
                        | Extract<DocumentScanTypePreviewEvent, { type: 'stream_error' }>
                        | undefined;
                    return (
                        <li
                            key={stage.id}
                            className={classNames(styles.testTimelineItem, styles[`testTimeline${humanize(status)}`])}
                            aria-current={status === 'running' ? 'step' : undefined}
                        >
                            <div className={styles.testTimelineRail}>
                                <div className={styles.testTimelineNumber}>{index + 1}</div>
                            </div>
                            <div className={styles.testTimelineCard}>
                                <button
                                    type="button"
                                    className={styles.testTimelineHeader}
                                    onClick={() => onExpand(stage.id)}
                                    aria-expanded={expanded}
                                >
                                    <div className={styles.testTimelineTitle}>
                                        <Icon name={stage.icon} type="fa" aria-hidden="true" />
                                        <span>
                                            <strong>{stage.title}</strong>
                                            <small>{stage.description}</small>
                                        </span>
                                    </div>
                                    <div className={styles.testTimelineStatus}>
                                        <StatusIndicator status={status} />
                                        <span>{statusLabel(status)}</span>
                                    </div>
                                </button>
                                {expanded ? (
                                    <div className={styles.testTimelineContent}>
                                        {error || (status === 'failed' && streamError) ? (
                                            <div className={styles.testFriendlyError}>
                                                {getFriendlyError(
                                                    error || {
                                                        stage:
                                                            stage.id === 'extraction'
                                                                ? 'analysis'
                                                                : stage.id === 'mapping'
                                                                ? 'previewPlanning'
                                                                : 'ocr',
                                                        message: streamError?.message || '',
                                                    },
                                                )}
                                            </div>
                                        ) : (
                                            <StageContent
                                                stage={stage.id}
                                                result={result}
                                                extractionSchema={extractionSchema}
                                            />
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ol>
        </section>
    );
}

export default function DocumentTypeTestModal({
    isOpen,
    onClose,
    actionLabel,
    configurationLabel,
    targetVersion,
    supportedEntityType,
    captureMode,
    captureConfig,
    extractionSchema,
    isTesting,
    result,
    onRunTest,
}: {
    isOpen: boolean;
    onClose: () => void;
    actionLabel: string;
    configurationLabel: string;
    targetVersion: DocumentScanTypeVersionSummary | null;
    supportedEntityType: DocumentScanSupportedEntityType;
    captureMode: DocumentScanCaptureMode;
    captureConfig: Record<string, unknown>;
    extractionSchema?: Record<string, unknown> | null;
    isTesting: boolean;
    result: DocumentScanTypeTestResult | null;
    onRunTest: (
        request: DocumentTypePreviewRequest,
        options: { signal: AbortSignal; onEvent: (event: DocumentScanTypePreviewEvent) => void },
    ) => Promise<void>;
}) {
    const [state, dispatch] = useReducer(reducer, {
        simulationPath: 'new_encounter',
        images: [],
        error: null,
        events: [],
        expandedStage: 'ocr',
    });
    const abortControllerRef = useRef<AbortController | null>(null);
    const slots = useMemo(() => getCaptureSlots(captureConfig), [captureConfig]);
    const isValidationOnly = supportedEntityType === 'encounter_form';
    const minImages = Math.max(1, Number(captureConfig.minImages || 1));
    const maxImages = Math.max(minImages, Number(captureConfig.maxImages || 10));
    const contextKey = `${targetVersion?.documentScanTypeVersionId || ''}:${configurationLabel}`;

    useEffect(() => {
        abortControllerRef.current?.abort();
        dispatch({ type: 'reset' });
    }, [contextKey]);

    useEffect(() => () => abortControllerRef.current?.abort(), []);

    const setUnnamedImages = async (files: FileList | null) => {
        try {
            const selected = Array.from(files || []);
            if (selected.length > maxImages) {
                dispatch({ type: 'error', error: `Upload no more than ${maxImages} images.` });
                return;
            }
            dispatch({
                type: 'images',
                images: await Promise.all(selected.map((file, index) => readImage(file, `page-${index + 1}`))),
            });
        } catch (error) {
            dispatch({ type: 'error', error: error instanceof Error ? error.message : String(error) });
        }
    };

    const setSlotImages = async (slot: CaptureSlot, files: FileList | null) => {
        try {
            const selected = Array.from(files || []);
            if (selected.length > slot.maxImages) {
                dispatch({ type: 'error', error: `${slot.label} accepts no more than ${slot.maxImages} images.` });
                return;
            }
            const retained = state.images.filter((image) => image.slotKey !== slot.slotKey);
            const next = await Promise.all(
                selected.map((file, index) => readImage(file, `${slot.slotKey}-${index + 1}`, slot)),
            );
            dispatch({ type: 'images', images: [...retained, ...next] });
        } catch (error) {
            dispatch({ type: 'error', error: error instanceof Error ? error.message : String(error) });
        }
    };

    const removeImage = (imageId: string) =>
        dispatch({ type: 'images', images: state.images.filter((image) => image.id !== imageId) });

    const run = async () => {
        if (!isValidationOnly && captureMode === 'unnamed_pages' && state.images.length < minImages) {
            dispatch({ type: 'error', error: `Upload at least ${minImages} image${minImages === 1 ? '' : 's'}.` });
            return;
        }
        if (!isValidationOnly && captureMode === 'named_slots') {
            const missing = slots.filter(
                (slot) => slot.required && !state.images.some((image) => image.slotKey === slot.slotKey),
            );
            if (missing.length) {
                dispatch({
                    type: 'error',
                    error: `Add an image for: ${missing.map((slot) => slot.label).join(', ')}.`,
                });
                return;
            }
        }
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        dispatch({ type: 'begin' });
        await onRunTest(
            { simulationPath: state.simulationPath, images: isValidationOnly ? [] : state.images },
            { signal: controller.signal, onEvent: (event) => dispatch({ type: 'event', event }) },
        );
    };

    const close = () => {
        abortControllerRef.current?.abort();
        onClose();
    };

    const renderUpload = (slot?: CaptureSlot) => {
        const images = slot ? state.images.filter((image) => image.slotKey === slot.slotKey) : state.images;
        return (
            <div className={styles.testUploadCard} key={slot?.slotKey || 'ordered-pages'}>
                <div className={styles.testUploadHeader}>
                    <span>
                        <strong>{slot?.label || 'Document images'}</strong>
                        <small>
                            {slot
                                ? `${slot.required ? 'Required' : 'Optional'} · Up to ${slot.maxImages} image${
                                      slot.maxImages === 1 ? '' : 's'
                                  }`
                                : `${minImages}–${maxImages} ordered image${maxImages === 1 ? '' : 's'}`}
                        </small>
                    </span>
                </div>
                <label className={styles.testDropzone}>
                    <input
                        className={styles.testUploadInput}
                        type="file"
                        accept={ACCEPTED_IMAGE_TYPES}
                        multiple={(slot?.maxImages || maxImages) > 1}
                        onChange={(event) =>
                            slot ? setSlotImages(slot, event.target.files) : setUnnamedImages(event.target.files)
                        }
                        data-test-id={
                            slot ? `document-type-test-slot-${slot.slotKey}` : 'document-type-test-image-input'
                        }
                    />
                    <Icon name={ICONS.Upload} size={24} aria-hidden="true" />
                    <span>
                        <strong>{images.length ? 'Replace selected images' : 'Choose images to test'}</strong>
                        <small>PNG, JPG, HEIC, or HEIF · Maximum 15 MB each</small>
                    </span>
                </label>
                {images.length ? (
                    <ol className={styles.testSelectedFiles}>
                        {images.map((image, index) => (
                            <li key={image.id}>
                                <span className={styles.testFileOrder}>{index + 1}</span>
                                <span>
                                    <strong>{image.fileName}</strong>
                                    <small>{formatFileSize(image.sizeBytes)}</small>
                                </span>
                                <button
                                    type="button"
                                    onClick={() => removeImage(image.id)}
                                    aria-label={`Remove ${image.fileName}`}
                                >
                                    <Icon name={ICONS.Close} size={10} aria-hidden="true" />
                                </button>
                            </li>
                        ))}
                    </ol>
                ) : null}
            </div>
        );
    };

    return (
        <SideModal isOpen={isOpen} isWide onClose={close}>
            <ModalHeader title={actionLabel} onClose={close} />
            <div className={styles.testModalBody} data-test-id="document-type-test-modal">
                <header className={styles.testIntro}>
                    <p>See how this version reads a document and prepares encounter information.</p>
                    <div className={styles.testContextPill}>
                        {configurationLabel} · Version {targetVersion?.versionNumber ?? '—'}
                    </div>
                </header>
                <div className={styles.testSafetyBanner}>
                    <Icon name="shield-alt" type="fa" aria-hidden="true" />
                    <span>
                        <strong>Preview only</strong>
                        No patient records will be created or changed.
                    </span>
                </div>

                {isValidationOnly ? (
                    <section className={styles.testValidationCard}>
                        <Icon name="clipboard-check" type="fa" aria-hidden="true" />
                        <div>
                            <h3>Check this configuration</h3>
                            <p>
                                Encounter-form document types do not read images in this test. Run the check to make
                                sure the selected version is ready to use.
                            </p>
                        </div>
                    </section>
                ) : (
                    <>
                        <section className={styles.testSetupSection}>
                            <div className={styles.testSectionHeading}>
                                <span>Choose what to preview</span>
                                <h3>What should happen after information is extracted?</h3>
                            </div>
                            <div className={styles.testChoiceGrid}>
                                <label
                                    className={classNames(styles.testChoiceCard, {
                                        [styles.testChoiceCardSelected]: state.simulationPath === 'new_encounter',
                                    })}
                                >
                                    <input
                                        type="radio"
                                        checked={state.simulationPath === 'new_encounter'}
                                        onChange={() =>
                                            dispatch({ type: 'simulationPath', simulationPath: 'new_encounter' })
                                        }
                                        data-test-id="document-type-test-path-new"
                                    />
                                    <Icon name="plus-circle" type="fa" aria-hidden="true" />
                                    <span>
                                        <strong>Create a new encounter</strong>
                                        <small>
                                            Preview the encounter number and information this configuration would
                                            create.
                                        </small>
                                    </span>
                                </label>
                                <label
                                    className={classNames(styles.testChoiceCard, {
                                        [styles.testChoiceCardSelected]: state.simulationPath === 'update_encounter',
                                    })}
                                >
                                    <input
                                        type="radio"
                                        checked={state.simulationPath === 'update_encounter'}
                                        onChange={() =>
                                            dispatch({ type: 'simulationPath', simulationPath: 'update_encounter' })
                                        }
                                        data-test-id="document-type-test-path-update"
                                    />
                                    <Icon name="edit" type="fa" aria-hidden="true" />
                                    <span>
                                        <strong>Update an encounter</strong>
                                        <small>
                                            Preview changes to a sample encounter. No real patient data is used.
                                        </small>
                                    </span>
                                </label>
                            </div>
                        </section>
                        <section className={styles.testSetupSection}>
                            <div className={styles.testSectionHeading}>
                                <span>Add a sample document</span>
                                <h3>Upload the images this version should read</h3>
                            </div>
                            <div className={styles.testUploadGrid}>
                                {captureMode === 'named_slots'
                                    ? slots.map((slot) => renderUpload(slot))
                                    : renderUpload()}
                            </div>
                            {state.images.length ? (
                                <button
                                    type="button"
                                    className={styles.testClearImages}
                                    onClick={() => dispatch({ type: 'images', images: [] })}
                                >
                                    Clear images
                                </button>
                            ) : null}
                        </section>
                    </>
                )}

                {state.error ? <div className={styles.testFriendlyError}>{state.error}</div> : null}
                {result?.validationErrors?.length ? (
                    <div className={styles.testFriendlyError}>
                        This configuration needs attention before it can be tested: {result.validationErrors.join('; ')}
                    </div>
                ) : null}

                {!isValidationOnly ? (
                    <TestTimeline
                        events={state.events}
                        result={result}
                        expandedStage={state.expandedStage}
                        onExpand={(stage) => dispatch({ type: 'expandedStage', stage })}
                        extractionSchema={extractionSchema}
                    />
                ) : null}

                {result?.status === 'preview_complete' ? <OutcomeCard result={result} /> : null}
                {result && !result.validationOnly ? <AdvancedResults result={result} /> : null}
                {result?.validationOnly && result.message ? (
                    <div className={styles.testValidationResult}>
                        <Icon name={ICONS.CheckCircle} size={22} aria-hidden="true" />
                        <span>{result.message}</span>
                    </div>
                ) : null}

                <div className={styles.testActions}>
                    <Button type="outline" text="Close" onClick={close} />
                    <Button
                        type="primary"
                        text={result ? 'Run again' : isValidationOnly ? 'Check configuration' : 'Run test'}
                        isLoading={isTesting}
                        disabled={!targetVersion}
                        onClick={run}
                        data-test-id="run-document-type-test-button"
                    />
                </div>
            </div>
        </SideModal>
    );
}
